import { defineLocalStore } from '@/utils/localStore'
import { computed, ref, watch } from 'vue'
import { handleAsync, setSnackbar } from 'best-modules/plugins'
import {
  DocumentTypeRoute,
  DocumentTypeRouteStage,
  DocumentTypeRouteStageListItem,
} from '@/utils/types/documentRoute'
import {
  docTypeRouteActivate,
  docTypeRouteDelete,
  docTypeRouteSingle,
  docTypeRouteList,
  docTypeRouteStageDelete,
  docTypeRouteStageParticipantCreate,
  docTypeRouteStageParticipantDelete,
} from '@/requests/documentType'
import { openDialog } from '@/dialog'

export const { initLocalStore: initRoutes, useLocalStore: useRoutes } =
  defineLocalStore('routes', (docTypeId: number) => {
    const routes = ref<
      Array<
        Omit<DocumentTypeRoute, 'stages'> & {
          stages: Array<DocumentTypeRouteStageListItem>
        }
      >
    >([])

    const getRoutes = () => {
      return docTypeRouteList(docTypeId).then(res => {
        routes.value.push(...res)
      })
    }

    const activeRouteId = computed<number | null>(() => {
      return routes.value.find(r => r.isActive)?.id ?? null
    })
    const activeRoute = ref<DocumentTypeRoute | null>(null)

    const getRouteSingle = () => {
      return handleAsync('getRouteSingle', () => {
        const id = activeRouteId.value
        if (id === null) return Promise.resolve()
        return docTypeRouteSingle(id).then(res => {
          activeRoute.value = res
        })
      })
    }

    watch(activeRouteId, id => {
      if (id) {
        getRouteSingle()
      } else {
        activeRoute.value = null
      }
    })

    const routeCreate = () => {
      openDialog({
        name: 'DocTypeRoute',
        action: 'create',
        dialogData: {
          route: {
            docTypeDefinitionId: docTypeId,
          },
        },
        params: {
          cb: r => {
            routes.value.push(r)
          },
        },
      })
    }

    const routeUpdate = (docTypeRoute: DocumentTypeRoute) => {
      openDialog({
        name: 'DocTypeRoute',
        action: 'update',
        dialogData: {
          route: docTypeRoute,
        },
        params: {
          cb: updatedRoute => {
            const r = routes.value.find(r => r.id == docTypeRoute.id)
            if (r) {
              Object.assign(r, updatedRoute)
            }

            if (activeRouteId.value === updatedRoute.id && activeRoute.value) {
              Object.assign(activeRoute.value, updatedRoute)
            }
          },
        },
      })
    }

    const routeDelete = (routeId: number) => {
      return docTypeRouteDelete(routeId).then(() => {
        routes.value = routes.value.filter(r => r.id !== routeId)
      })
    }

    const routeSetActive = async (routeId: number) => {
      await docTypeRouteActivate(routeId)

      const oldActiveRoute = routes.value.find(r => r.isActive)
      const newActiveRoute = routes.value.find(r => r.id == routeId)

      if (oldActiveRoute) {
        oldActiveRoute.isActive = false
      }
      if (newActiveRoute) {
        newActiveRoute.isActive = true
      }
    }

    const stageCreate = () => {
      if (!activeRoute.value) return
      openDialog({
        name: 'DocTypeRouteStage',
        action: 'create',
        dialogData: {
          stage: {
            docTypeRouteId: activeRouteId.value,
            sequence: activeRoute.value.stages.length + 1,
          },
          maxSequence: activeRoute.value.stages.length + 1,
          resource: 'administration',
        },
        params: {
          cb: stage => {
            const docTypeRoute = routes.value.find(
              r => r.id == activeRouteId.value
            )
            if (docTypeRoute) {
              docTypeRoute.stages.push(stage)
            }

            if (activeRoute.value) {
              activeRoute.value.stages.push(stage)
            }
          },
        },
      })
    }

    const stageUpdate = (stage: DocumentTypeRouteStage) => {
      if (!activeRoute.value) return
      openDialog({
        name: 'DocTypeRouteStage',
        action: 'update',
        dialogData: {
          stage: stage,
          maxSequence: activeRoute.value.stages.length + 1,
          resource: 'administration',
        },
        params: {
          cb: updatedStage => {
            const docTypeRoute = routes.value.find(
              r => r.id == activeRouteId.value
            )
            if (docTypeRoute) {
              docTypeRoute.stages = docTypeRoute.stages.map(s =>
                s.id === updatedStage.id ? updatedStage : s
              )
            }
            if (activeRoute.value) {
              activeRoute.value.stages = activeRoute.value.stages.map(s =>
                s.id === updatedStage.id ? updatedStage : s
              )
            }
          },
        },
      })
    }

    const stageDelete = (stageId: number) => {
      return handleAsync(`stageDelete_${stageId}`, () => {
        return docTypeRouteStageDelete(stageId).then(() => {
          const docTypeRoute = routes.value.find(
            r => r.id == activeRouteId.value
          )
          if (docTypeRoute) {
            docTypeRoute.stages = docTypeRoute.stages.filter(
              s => s.id !== stageId
            )
          }

          if (activeRoute.value) {
            activeRoute.value.stages = activeRoute.value.stages.filter(
              s => s.id !== stageId
            )
          }
        })
      })
    }

    const stageParticipantCreate = (stageId: number, userId: number) => {
      return docTypeRouteStageParticipantCreate({
        docTypeRouteStageId: stageId,
        userId,
      }).then(res => {
        const stage = activeRoute.value?.stages.find(s => s.id === stageId)
        stage?.participants.push(res)
        setSnackbar({ text: 'Учасника успішно додано' })
      })
    }

    const stageParticipantDelete = (stageId: number, participantId: number) => {
      return docTypeRouteStageParticipantDelete(participantId).then(() => {
        const stage = activeRoute.value?.stages.find(s => s.id === stageId)
        if (stage) {
          stage.participants = stage.participants.filter(
            s => s.id !== participantId
          )
        }
      })
    }

    getRoutes()

    return {
      docTypeId,
      routes,
      getRoutes,
      activeRouteId,
      activeRoute,
      getRouteSingle,
      routeCreate,
      routeUpdate,
      routeDelete,
      routeSetActive,

      // stage
      stageCreate,
      stageUpdate,
      stageDelete,

      // participant
      stageParticipantCreate,
      stageParticipantDelete,
    }
  })
